import React, { useCallback, useState } from "react";
import { Anchor, Button, Group, Stack, Text } from "@mantine/core";
import { IconExternalLink } from "@tabler/icons-react";
import { isSafeHttpsUrl } from "../../utils/releaseNotes";
import classes from "./ReleaseNoteToast.module.css";

/** Longest Refresh waits for the mark-read PUT before reloading anyway. */
export const REFRESH_MARK_READ_TIMEOUT_MS = 2000;

export interface ReleaseNoteToastProps {
  summary: string;
  link?: string | null;
  /** Persist the note as read; awaited (up to 2s) before the page reloads. */
  onRefresh: () => Promise<void> | void;
}

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/** Mantine Notification classNames that give the release-note toast its shell. */
export const releaseNoteToastClassNames = {
  root: classes.toast,
  title: classes.toastTitle,
};

/**
 * Body of the "new version available" toast (see notifyReleaseNote). Refresh
 * marks the note read, then reloads so the tab picks up the new release. The
 * toast has no close button: Refresh is the only way to clear it.
 */
export const ReleaseNoteToast: React.FC<ReleaseNoteToastProps> = ({
  summary,
  link,
  onRefresh,
}) => {
  const [refreshing, setRefreshing] = useState(false);
  const safeLink = isSafeHttpsUrl(link) ? link : null;

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.race([
        Promise.resolve().then(onRefresh),
        wait(REFRESH_MARK_READ_TIMEOUT_MS),
      ]);
    } catch (error) {
      console.error("Failed to mark release note as read:", error);
    }
    window.location.reload();
  }, [onRefresh]);

  return (
    <Stack gap="xs">
      {summary ? (
        <Text size="sm" className={classes.summary}>
          {summary}
        </Text>
      ) : null}
      <Group gap="md" align="center">
        <Button
          size="xs"
          fz="sm"
          color="burntOrange"
          className={classes.refreshButton}
          loading={refreshing}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
        {safeLink ? (
          <Anchor
            href={safeLink}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            className={classes.link}
          >
            What&apos;s new
            <IconExternalLink size={14} aria-hidden="true" />
          </Anchor>
        ) : null}
      </Group>
    </Stack>
  );
};

export default ReleaseNoteToast;
